import React from "react";
import { EVENT_LOCALE_SET, events } from "../embed/events";
import type { LocaleState } from "../lib/locale";
import { DEFAULT_LOCALE, formatMoney, normalizeLocale, setRuntimeLocale, t as tRuntime } from "../lib/locale";

const SPA_LANGUAGE_KEY = "ww/spa/language" as const;
const SPA_MARKET_KEY = "ww/spa/market" as const;

type LocaleContextValue = LocaleState & {
  t: (key: string) => string;
  formatMoney: (amount?: number) => string;
};

const LocaleContext = React.createContext<LocaleContextValue>({
  ...DEFAULT_LOCALE,
  t: (key: string) => tRuntime(key, DEFAULT_LOCALE),
  formatMoney: (amount?: number) => formatMoney(amount, DEFAULT_LOCALE),
});

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = React.useState<LocaleState>(() => {
    // SPA-only: allow overriding locale via persisted settings (does not affect widget embed).
    try {
      const lang = window.localStorage.getItem(SPA_LANGUAGE_KEY);
      const market = window.localStorage.getItem(SPA_MARKET_KEY);
      if ((lang && lang.trim()) || (market && market.trim())) {
        return normalizeLocale({
          language: lang && lang.trim() ? lang.trim() : undefined,
          market: market && market.trim() ? market.trim() : undefined,
        });
      }
    } catch {}

    // Best-effort: start from runtime config if present, else defaults.
    try {
      const w = window as any;
      const cfg = w?.__WW__;
      return normalizeLocale({ language: cfg?.language, market: cfg?.market });
    } catch {
      return DEFAULT_LOCALE;
    }
  });

  React.useEffect(() => {
    // Keep runtime config in sync for non-React code paths.
    setRuntimeLocale(locale);
  }, [locale]);

  React.useEffect(() => {
    const handler = (e: CustomEvent) => {
      const next = normalizeLocale((e?.detail || {}) as Partial<LocaleState>);
      setLocale(next);
    };
    events.on(EVENT_LOCALE_SET, handler);
    return () => events.off(EVENT_LOCALE_SET, handler);
  }, []);

  const value = React.useMemo<LocaleContextValue>(() => {
    return {
      ...locale,
      t: (key: string) => tRuntime(key, locale),
      formatMoney: (amount?: number) => formatMoney(amount, locale),
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  return React.useContext(LocaleContext);
}


